import { useState, useEffect, lazy, Suspense } from 'react';
import {
  BuilderCanvas,
  LayerPanel,
  LeftIconNav,
  NodeProperties,
  SurfaceTabs,
  PriceIndicator,
  SurfaceOptions,
  ProductPanel,
  ColorPicker,
  ToolsBar,
} from './components';
const DesignLibraryLazy = lazy(() =>
  import('./components/DesignLibrary').then((m) => ({
    default: m.DesignLibrary,
  })),
);
import type { BuilderSidebarView } from './components';
import { FabricCanvasProvider } from './contexts/FabricCanvasContext';
import { BuilderApiProvider, useBuilderApi } from './contexts/BuilderApiContext';
import { useBuilderState } from './hooks/useBuilderState';
import { useBuilderNode } from './hooks/useBuilderNode';
import { useBuilderStore } from './store/builderStore';
import { usePricing } from './hooks/usePricing';
import { exportSurfaceImages } from './utils/exportUtils';
import { loadAllBuilderFonts, loadGoogleFont, normalizeWeight } from './lib/fonts';
import { BuilderSkeleton } from './components/BuilderSkeleton/BuilderSkeleton';
import type { BuilderApiAdapter } from './types/api.types';
import {
  Undo2,
  Redo2,
  Save,
  Loader2,
  X,
  Check,
} from 'lucide-react';
import { AlertDialogPrimitive, Button } from '@printtat/ui';

export interface DesignBuilderProps {
  /** API adapter — each app provides its own */
  api: BuilderApiAdapter;
  /** Product ID or slug to initialize from (new design) */
  productId?: string;
  /** Existing design ID to load and edit */
  designId?: string;
  /** Product slug for skeleton garment silhouette during loading. If omitted, shows generic placeholder. */
  productSlug?: string;
  /** Called after successful save with the design ID */
  onSave?: (designId: string) => void;
  /** Called when user closes the builder */
  onClose?: () => void;
  /** URL for "Manage Library" link in DesignLibrary sidebar. Hidden if omitted. */
  manageLibraryUrl?: string;
}

const BuilderInner = ({
  productId: productIdProp,
  designId: designIdProp,
  productSlug,
  onSave,
  onClose,
  manageLibraryUrl,
}: Omit<DesignBuilderProps, 'api'>) => {
  const api = useBuilderApi();
  const [activeView, setActiveView] = useState<BuilderSidebarView>('design');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const handleViewChange = (view: BuilderSidebarView) => {
    setActiveView((prev) => (prev === view ? prev : view));
  };
  const pricing = usePricing();
  const {
    undo,
    redo,
    history,
    exportDesign,
    selectedNodeId,
  } = useBuilderState();
  const { deleteNode } = useBuilderNode();

  const designId = useBuilderStore((s) => s.designId);
  const storeProductId = useBuilderStore((s) => s.productId);
  const isDirty = useBuilderStore((s) => s.isDirty);
  const initFromProduct = useBuilderStore((s) => s.initFromProduct);
  const initFromDesign = useBuilderStore((s) => s.initFromDesign);
  const markSaved = useBuilderStore((s) => s.markSaved);

  // Track the active design ID (may be set after first save of a new design)
  const [activeDesignId, setActiveDesignId] = useState<string | null>(designIdProp ?? null);

  useEffect(() => {
    let cancelled = false;

    loadAllBuilderFonts();

    if (designIdProp) {
      api
        .getDesign(designIdProp)
        .then(async (design) => {
          if (cancelled) return;

          // Preload fonts used in the design
          const canvasData = design.canvasData as {
            surfaces?: Record<string, { type: string; data: { fontFamily?: string; fontWeight?: string | number } }[]>;
          };
          const fontLoads: Promise<void>[] = [];
          const seen = new Set<string>();
          for (const nodes of Object.values(canvasData.surfaces ?? {})) {
            for (const node of nodes) {
              if (node.type === 'text' && node.data.fontFamily) {
                const weight = normalizeWeight(node.data.fontWeight as 'normal' | 'bold' | number | undefined);
                const key = `${node.data.fontFamily}:${weight}`;
                if (!seen.has(key)) {
                  seen.add(key);
                  fontLoads.push(loadGoogleFont(node.data.fontFamily, weight));
                }
              }
            }
          }
          await Promise.all(fontLoads);

          if (!cancelled) initFromDesign(design);
        })
        .catch(() => {
          if (!cancelled) setLoadError('Failed to load design');
        })
        .finally(() => {
          if (!cancelled) setIsLoadingData(false);
        });
    } else if (productIdProp) {
      api
        .getProduct(productIdProp)
        .then((product) => {
          if (!cancelled) initFromProduct(product);
        })
        .catch(() => {
          if (!cancelled) setLoadError('Failed to load product');
        })
        .finally(() => {
          if (!cancelled) setIsLoadingData(false);
        });
    } else {
      onClose?.();
    }

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Delete key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (!selectedNodeId) return;
      e.preventDefault();
      deleteNode(selectedNodeId);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, deleteNode]);

  // Warn on browser close when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveStatus('idle');

    try {
      const state = useBuilderStore.getState();
      const canvasData = {
        surfaces: state.surfaces,
        surfaceOptions: state.surfaceOptionSelections,
        enabledColors: state.enabledColors,
        enabledSizes: state.enabledSizes,
        defaultColor: state.defaultColor,
        defaultSize: state.defaultSize,
      };

      const pricingFields: {
        totalPrice?: number;
        surfacesUsed?: string[];
        priceBreakdown?: import('@printtat/shared').PriceBreakdown;
      } = pricing
        ? {
            totalPrice: pricing.total,
            surfacesUsed: pricing.surfaces
              .filter((s) => s.hasDesign)
              .map((s) => s.surface),
            priceBreakdown: pricing,
          }
        : {};

      let currentDesignId = activeDesignId ?? designId;

      // For new designs, create first
      if (!currentDesignId && storeProductId) {
        const result = await api.createDesign({
          productId: storeProductId,
          name: state.designName,
          description: state.designDescription || undefined,
          canvasData,
          ...pricingFields,
        });
        currentDesignId = result.id;
        markSaved(result.id);
        setActiveDesignId(result.id);
      }

      if (currentDesignId) {
        // Export surfaces as PNGs
        let surfaceExports: Record<string, string> | undefined;
        try {
          const blobs = await exportSurfaceImages(
            state.surfaces,
            state.productConfig.dimensions,
          );
          if (Object.keys(blobs).length > 0) {
            const entries = await Promise.all(
              Object.entries(blobs).map(async ([surface, blob]) => {
                const file = new File([blob], `${surface}.png`, { type: 'image/png' });
                const result = await api.uploadSurfaceExport(file, currentDesignId!);
                return [surface, result.url] as const;
              }),
            );
            surfaceExports = Object.fromEntries(entries);
          }
        } catch (err) {
          console.warn('Surface export failed, saving without exports:', err);
        }

        await api.updateDesign(currentDesignId, {
          name: state.designName,
          description: state.designDescription || undefined,
          canvasData,
          ...pricingFields,
          ...(surfaceExports ? { surfaceExports } : {}),
        });

        onSave?.(currentDesignId);
      } else {
        // Fallback: localStorage
        localStorage.setItem('builder-design', exportDesign());
      }

      useBuilderStore.setState({ isDirty: false });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      alert('Failed to save design');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      onClose?.();
    }
  };

  if (isLoadingData) {
    return <BuilderSkeleton productSlug={productSlug} />;
  }

  if (loadError) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-50 gap-4">
        <p className="text-gray-500">{loadError}</p>
        <button
          onClick={() => onClose?.()}
          className="text-sm text-gray-900 underline cursor-pointer"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50 overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 relative min-h-0 min-w-0 flex">
          {/* Floating left sidebar */}
          <div className="absolute top-4 bottom-4 left-4 z-10 flex overflow-hidden bg-white rounded-lg border border-gray-200 shadow-lg">
            <LeftIconNav activeView={activeView} onViewChange={handleViewChange} />
            <div className="w-52 flex flex-col overflow-hidden">
              {activeView === 'product' && (
                <div className="flex-1 overflow-y-auto min-h-0">
                  <ProductPanel />
                </div>
              )}
              {activeView === 'assets' && (
                <div className="flex-1 overflow-y-auto min-h-0">
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={16} className="animate-spin text-gray-400" />
                      </div>
                    }
                  >
                    <DesignLibraryLazy manageLibraryUrl={manageLibraryUrl} />
                  </Suspense>
                </div>
              )}
              {activeView === 'design' && (
                <div className="flex-1 overflow-y-auto min-h-0">
                  <LayerPanel />
                </div>
              )}
            </div>
          </div>
          <BuilderCanvas />
          <div className="absolute top-9 left-[284px] z-10">
            <ToolsBar />
          </div>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5">
            <SurfaceTabs />
            <SurfaceOptions />
            <ColorPicker />
          </div>
          <div className="absolute top-4 bottom-4 right-4 z-10 w-56 flex flex-col items-stretch gap-2">
            <div className="flex flex-row w-full gap-1">
              <div className="px-0.75 flex-1 flex items-center justify-between border rounded-full bg-white shadow-xs p-0.5">
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={undo}
                    disabled={!canUndo}
                    className="px-1.5 py-0.5 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    title="Undo"
                  >
                    <Undo2 size={14} className="text-gray-600" />
                  </button>
                  <button
                    onClick={redo}
                    disabled={!canRedo}
                    className="px-1.5 py-0.5 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    title="Redo"
                  >
                    <Redo2 size={14} className="text-gray-600" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-md hover:bg-gray-100 disabled:opacity-50 transition-colors"
                    title="Save design"
                  >
                    {isSaving ? (
                      <Loader2 size={14} className="text-gray-600 animate-spin" />
                    ) : saveStatus === 'saved' ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <Save size={14} className="text-gray-600" />
                    )}
                    <span className="text-[10px] text-gray-600">
                      {isSaving ? 'Saving' : saveStatus === 'saved' ? 'Saved' : 'Save'}
                    </span>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between border rounded-full bg-white shadow-xs p-0.5">
                <button
                  onClick={handleClose}
                  className="rounded-md hover:bg-gray-100 transition-colors h-[20px] w-[20px] text-center items-center justify-center flex"
                  title="Close builder"
                >
                  <X size={14} className="text-gray-600" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <NodeProperties />
            </div>
          </div>
          <div className="absolute bottom-4 left-[284px] z-10 w-56">
            <PriceIndicator defaultOpen={false} />
          </div>
        </div>
      </div>

      {/* Unsaved changes dialog */}
      <AlertDialogPrimitive.Root open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <AlertDialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg">
            <AlertDialogPrimitive.Title className="text-lg font-semibold text-gray-900">
              Unsaved changes
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description className="text-sm text-gray-600">
              You have unsaved changes. What would you like to do?
            </AlertDialogPrimitive.Description>
            <div className="flex justify-end gap-3">
              <AlertDialogPrimitive.Cancel asChild>
                <Button variant="outline">Cancel</Button>
              </AlertDialogPrimitive.Cancel>
              <AlertDialogPrimitive.Action asChild>
                <Button
                  color="red"
                  onClick={() => { setShowUnsavedDialog(false); onClose?.(); }}
                >
                  Discard
                </Button>
              </AlertDialogPrimitive.Action>
              <AlertDialogPrimitive.Action asChild>
                <Button
                  color="dark"
                  onClick={async () => {
                    setShowUnsavedDialog(false);
                    await handleSave();
                    onClose?.();
                  }}
                >
                  Save & Close
                </Button>
              </AlertDialogPrimitive.Action>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialogPrimitive.Root>
    </div>
  );
};

export const DesignBuilder = ({ api, ...rest }: DesignBuilderProps) => {
  return (
    <BuilderApiProvider api={api}>
      <FabricCanvasProvider>
        <BuilderInner {...rest} />
      </FabricCanvasProvider>
    </BuilderApiProvider>
  );
};
